import React from 'react';
import { BannerProps, money } from './BannerProps';

/**
 * Design 8: coupon. A dashed-edge ticket the customer can quote on the
 * phone. Owner-approved mockup #08.
 *
 * The mockup's right-hand panel shows a redemption code (e.g. "THANKS25").
 * `CustomerPromotion.promoCode` carries that when the promotion has one
 * (joined server-side from `promo_codes` via `promo_code_id`). A promotion
 * can exist with no code attached, though, so this still falls back to
 * showing the discount itself (the $ saved, plus the percent off when a
 * row states one) rather than an empty panel.
 */
const CouponBanner: React.FC<BannerProps> = ({ promotion, brandColor = '#1e5aa8' }) => {
  const rowSaving = promotion.rows.reduce(
    (total, row) => total + (row.wasCents !== null ? row.wasCents - row.priceCents : 0),
    0,
  );
  const totalsSaving =
    promotion.wasTotalCents !== null && promotion.totalCents !== null
      ? promotion.wasTotalCents - promotion.totalCents
      : null;
  const saving = totalsSaving ?? rowSaving;
  const percentOff = promotion.rows.find((row) => row.percentOff !== null)?.percentOff ?? null;
  const expires = promotion.finePrint ?? (promotion.endsAt ? `Expires ${promotion.endsAt}` : null);

  return (
    <div
      style={{
        display: 'flex',
        border: '1.5px dashed ' + brandColor,
        borderRadius: 6,
        overflow: 'hidden',
        fontFamily: 'Helvetica, Arial, sans-serif',
        color: '#111',
        flexWrap: 'wrap',
      }}
    >
      <div style={{ flex: '1 1 200px', padding: '12px 14px', minWidth: 0 }}>
        <div style={{ fontSize: 14, fontWeight: 800, overflowWrap: 'break-word' }}>
          {promotion.headline ?? promotion.name}
        </div>
        {promotion.body && (
          <div style={{ fontSize: 12, color: '#5b6470', marginTop: 4, overflowWrap: 'break-word' }}>{promotion.body}</div>
        )}
        {expires && <div style={{ fontSize: 10.5, color: '#6b7580', marginTop: 8 }}>{expires}</div>}
      </div>
      <div
        style={{
          flex: '0 0 104px',
          borderLeft: '1.5px dashed ' + brandColor,
          background: '#f2f7fd',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          padding: 10,
        }}
      >
        {promotion.promoCode ? (
          <>
            <div style={{ fontSize: 10, letterSpacing: '0.1em', color: '#5b6470' }}>CODE</div>
            <div style={{ fontSize: 13, fontWeight: 800, letterSpacing: '0.05em', color: brandColor, overflowWrap: 'break-word', textAlign: 'center' }}>
              {promotion.promoCode}
            </div>
          </>
        ) : (
          <>
            <div style={{ fontSize: 10, letterSpacing: '0.1em', color: '#5b6470' }}>SAVE</div>
            <div style={{ fontSize: 15, fontWeight: 800, color: brandColor }}>{money(saving)}</div>
            {percentOff !== null && <div style={{ fontSize: 10.5, color: '#5b6470' }}>({percentOff}% off)</div>}
          </>
        )}
      </div>
    </div>
  );
};

export default CouponBanner;
